import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  CheckIcon,
  ChevronRightIcon,
  ClockIcon,
  CopyIcon,
  DownloadIcon,
  FileIcon,
  ImageIcon,
  InfoIcon,
  PaperclipIcon,
  Trash2Icon,
} from "lucide-react";

import { api, getAdminToken, type AttachmentMeta } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { useDeleteReport, useProject, useReport, useUpdateReport } from "../app/queries";
import { ProjectTabs } from "../components/ProjectTabs";
import { Topbar } from "../components/Topbar";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ErrorState, ProjectNotFound } from "../components/ErrorState";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from "../components/ui/skeleton";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import {
  formatExact,
  relativeTime,
  shortDate,
} from "../lib/format";
import { cn } from "../lib/utils";
import { Page, PageBody, PageHeader, PageTitle, PageDescription } from "../components/PageLayout";

export function ProjectReportDetailPage() {
  const navigate = useNavigate();
  const { id = "", reportId = "" } = useParams();
  const projects = useProject(id);
  const reports = useReport(id, reportId);

  const project = projects.project;
  const report = reports.data;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const remove = useDeleteReport(id, {
    success: "Report deleted",
    error: (err) => err instanceof Error ? err.message : "Failed to delete report",
  });
  const update = useUpdateReport(id, reportId, {
    success: (report) => {
      // The page calls mutate({status}) / mutate({severity}) from
      // separate handlers; surface a targeted toast that names the
      // field that just changed.
      const next = update.variables;
      if (next?.status) return `Status set to ${next.status}`;
      if (next?.severity) return `Severity set to ${next.severity}`;
      return `Saved ${report.title}`;
    },
    error: (err) =>
      err instanceof Error ? err.message : "Failed to update report",
  });

  if (projects.isLoading || reports.isLoading) {
    return (
      <Page>
        <Topbar title="…" />
        <PageBody>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (!project) {
    return <ProjectMissing />;
  }

  const reportsBreadcrumb = [
    { label: "Projects", to: "/" },
    { label: project.name, to: `/projects/${id}` },
    { label: "Reports" },
  ];

  if (reports.isError) {
    return (
      <ErrorState
        title="Report unavailable"
        description={reports.error.message}
        actionLabel="Back to reports"
        to={`/projects/${id}/reports`}
        topbarTitle={project.name}
        breadcrumb={reportsBreadcrumb}
        tabs={<ProjectTabs />}
      />
    );
  }

  if (!report) {
    return (
      <ErrorState
        title="No such report"
        description="The report you requested does not exist or has been removed."
        actionLabel="Back to reports"
        to={`/projects/${id}/reports`}
        topbarTitle={project.name}
        breadcrumb={reportsBreadcrumb}
        tabs={<ProjectTabs />}
      />
    );
  }

  return (
    <Page>
      <Topbar
        subtitle={report.reference}
        breadcrumb={[
          { label: "Projects", to: "/" },
          { label: project.name, to: `/projects/${id}` },
          { label: "Reports", to: `/projects/${id}/reports` },
          { label: report.reference },
        ]}
      />
      <ProjectTabs />
      <PageHeader>
        <div className="flex flex-col gap-3">
          <Button
            render={<Link to={`/projects/${id}/reports`} />}
            nativeButton={false}
            type="button"
            variant="ghost"
            size="sm"
            className="self-start text-muted-foreground"
          >
            <ArrowLeftIcon /> All reports
          </Button>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex min-w-0 flex-col">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-mono">{report.reference}</span>
                <span>·</span>
                <span>{report.source || "unknown"}</span>
                <span>·</span>
                <span>{report.environment || "unspecified"}</span>
              </div>
              <PageTitle>{report.title || "Untitled report"}</PageTitle>
              {report.description ? (
                <PageDescription className="mt-2 max-w-3xl">
                  {report.description}
                </PageDescription>
              ) : null}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <ClockIcon className="size-3.5" />
                  received{" "}
                  <span
                    className="font-mono text-foreground/80"
                    title={formatExact(report.receivedAt)}
                  >
                    {relativeTime(report.receivedAt)}
                  </span>
                </span>
                <span aria-hidden>·</span>
                <span>
                  updated {shortDate(report.updatedAt)}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                data-testid="delete-report"
              >
                <Trash2Icon /> Delete
              </Button>
              <StatusEditor
                value={report.status}
                busy={
                  update.isPending && update.variables?.status !== undefined
                }
                onChange={(status) => update.mutate({ status })}
                testIdPrefix="status"
              />
              <SeverityEditor
                value={report.severity}
                busy={
                  update.isPending &&
                  update.variables?.severity !== undefined
                }
                onChange={(severity) => update.mutate({ severity })}
                testIdPrefix="severity"
              />
            </div>
          </div>
        </div>
      </PageHeader>

      <PageBody data-testid="report-detail" width="wide">
        <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <Card className="xl:col-span-2">
            <CardHeader className="border-b">
              <CardTitle>Environment</CardTitle>
              <CardDescription>
                Build metadata submitted by the game client. Anything the
                player didn't supply is shown as "—".
              </CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                <Meta label="Game version" value={report.gameVersion || "—"} monospace />
                <Meta label="Build" value={report.buildNumber || "—"} monospace />
                <Meta label="Platform" value={report.platform || "—"} monospace />
                <Meta label="Source" value={report.source || "—"} />
                <Meta label="Player ref" value={report.playerRef || "—"} monospace />
                <Meta label="External id" value={report.externalId || "—"} monospace />
                <Meta
                  label="Environment"
                  value={report.environment || "—"}
                />
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="border-b">
              <CardTitle>Activity</CardTitle>
              <CardDescription>
                Status and severity changes for this report.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {update.isError ? (
                <Alert variant="destructive" data-testid="update-error" className="m-0">
                  <AlertDescription>
                    {update.error.message}
                  </AlertDescription>
                </Alert>
              ) : null}
              {report.activity && report.activity.length > 0 ? (
                <ul className="divide-y">
                  {report.activity.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex flex-col gap-1.5 px-5 py-3 text-sm"
                    >
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                        <span>{entry.type.replace("_", " ")}</span>
                        <span aria-hidden>·</span>
                        <span className="capitalize">{entry.field}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {entry.oldValue ? (
                          <span className="font-mono text-xs text-muted-foreground line-through">
                            {entry.oldValue}
                          </span>
                        ) : null}
                        {entry.oldValue ? (
                          <ArrowLeftIcon className="size-3 text-muted-foreground/70 [transform:rotate(180deg)]" />
                        ) : null}
                        <span className="font-mono text-xs text-foreground">
                          {entry.newValue || ""}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground" title={entry.createdAt}>
                          {relativeTime(entry.createdAt)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-none px-5 py-6 text-sm text-muted-foreground">
                  No dashboard activity yet. Status and severity changes are
                  logged here.
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <Card>
          <CardHeader className="border-b">
            <CardTitle>Payload</CardTitle>
            <CardAction>
              <span className="text-xs text-muted-foreground">
                {report.payloadJson && report.payloadJson !== "{}"
                  ? `${safeJsonLength(report.payloadJson)} chars`
                  : "empty"}
              </span>
            </CardAction>
          </CardHeader>
          <CardContent>
            <PayloadViewer payload={report.payloadJson} />
          </CardContent>
        </Card>

        <AttachmentList attachments={report.attachments} />
      </PageBody>

      <ConfirmDialog
        open={deleteOpen}
        title="Delete report"
        message={
          <>
            This permanently deletes <strong>{report.reference}</strong> and all
            of its uploaded attachments. This action cannot be undone.
          </>
        }
        confirmLabel="Delete report"
        destructive
        busy={remove.isPending}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(report.id);
            setDeleteOpen(false);
            navigate(`/projects/${id}/reports`, { replace: true });
          } catch {
            // Error toast is handled by useDeleteReport.
          }
        }}
      />
    </Page>
  );
}

function ProjectMissing() {
  return <ProjectNotFound />;
}

function Meta({
  label,
  value,
  monospace,
}: {
  label: string;
  value: string;
  monospace?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "truncate text-sm text-foreground",
          monospace && "font-mono",
        )}
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

function safeJsonLength(json: string): number {
  try {
    return JSON.stringify(JSON.parse(json)).length;
  } catch {
    return json.length;
  }
}

function PayloadViewer({ payload }: { payload: string }) {
  const parsed = useMemo(() => {
    if (!payload || payload === "{}") return null;
    try {
      return JSON.parse(payload) as unknown;
    } catch {
      return null;
    }
  }, [payload]);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  if (parsed === null) {
    return (
      <div className="rounded-md border border-dashed border-border bg-background px-3 py-6 text-sm text-muted-foreground">
        No payload submitted.
      </div>
    );
  }

  const toggle = (path: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCollapsed(new Set(collectPaths(parsed, "$")))}
        >
          Collapse all
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCollapsed(new Set())}
        >
          Expand all
        </Button>
        <CopyButton
          className="ml-auto"
          text={JSON.stringify(parsed, null, 2)}
          label="Copy JSON"
          iconButton
        />
      </div>
      <div className="overflow-x-auto rounded-md border bg-background px-4 py-3">
        <JsonNode
          value={parsed}
          path="$"
          depth={0}
          collapsed={collapsed}
          onToggle={toggle}
        />
      </div>
    </div>
  );
}

function collectPaths(value: unknown, base: string): string[] {
  if (value === null || typeof value !== "object") return [base];
  const out: string[] = [];
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const path = `${base}.${k}`;
    out.push(path);
    if (v !== null && typeof v === "object") {
      out.push(...collectPaths(v, path));
    }
  }
  return out;
}

function JsonNode({
  value,
  path,
  depth,
  collapsed,
  onToggle,
}: {
  value: unknown;
  path: string;
  depth: number;
  collapsed: Set<string>;
  onToggle: (path: string) => void;
}) {
  const [padLeft, setPadLeft] = useState(12);
  useEffect(() => {
    setPadLeft(depth === 0 ? 0 : 12);
  }, [depth]);

  if (value === null || typeof value !== "object") {
    return <ScalarNode value={value} path={path} />;
  }

  const entries = Object.entries(value as Record<string, unknown>);
  const isCollapsed = collapsed.has(path) && entries.length > 0;
  const isArray = Array.isArray(value);
  const summary = isArray
    ? `[${entries.length} item${entries.length === 1 ? "" : "s"}]`
    : `{${entries.length} field${entries.length === 1 ? "" : "s"}}`;

  return (
    <div style={{ marginLeft: padLeft }}>
      <div className="flex items-center gap-2">
        {entries.length > 0 ? (
          <button
            type="button"
            onClick={() => onToggle(path)}
            aria-label={isCollapsed ? "Expand" : "Collapse"}
            className="grid size-4 shrink-0 place-items-center rounded text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {isCollapsed ? "+" : "−"}
          </button>
        ) : (
          <span className="inline-block size-4" />
        )}
        <span className="font-mono text-xs text-muted-foreground">
          {isCollapsed ? summary : isArray ? "[" : "{"}
        </span>
        <CopyButton text={path} label="copy path" iconButton />
      </div>
      {!isCollapsed ? (
        <div>
          {entries.map(([k, v], idx) => (
            <div key={k} className="ml-4">
              <span className="font-mono text-xs text-muted-foreground">
                "{k}"
              </span>
              <span className="font-mono text-xs text-muted-foreground">: </span>
              <JsonNode
                value={v}
                path={`${path}.${k}`}
                depth={depth + 1}
                collapsed={collapsed}
                onToggle={onToggle}
              />
              {idx < entries.length - 1 ? (
                <span className="font-mono text-xs text-muted-foreground">
                  ,
                </span>
              ) : null}
            </div>
          ))}
          <div className="-ml-4">
            <span className="font-mono text-xs text-muted-foreground">
              {isArray ? "]" : "}"}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ScalarNode({ value, path }: { value: unknown; path: string }) {
  const display = formatScalar(value);
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono text-xs text-foreground">{display}</span>
      <CopyButton text={JSON.stringify(value)} label="copy" iconButton />
      <span className="hidden">{path}</span>
    </span>
  );
}

function formatScalar(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return `"${value}"`;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return JSON.stringify(value);
}

function CopyButton({
  text,
  label,
  className,
  iconButton,
}: {
  text: string;
  label: string;
  className?: string;
  iconButton?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  async function onClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    const result = await copyToClipboard(text);
    if (!result.ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }
  if (iconButton) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={copied ? "Copied" : label}
        className={cn(
          "grid size-6 shrink-0 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground",
          className,
        )}
      >
        {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
        <span className="sr-only">{label}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground",
        className,
      )}
      title={label}
    >
      {copied ? "copied" : label}
    </button>
  );
}

function AttachmentList({
  attachments,
}: {
  attachments: AttachmentMeta[];
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (attachments.length === 0) {
    return (
      <div
        data-testid="attachment-list"
        className="rounded-md border border-dashed border-border bg-background px-3 py-6 text-sm text-muted-foreground"
      >
        This report did not include any attachments.
      </div>
    );
  }
  const active = openId
    ? attachments.find((a) => a.id === openId) ?? null
    : null;
  return (
    <>
      <ul
        data-testid="attachment-list"
        className="flex flex-col divide-y rounded-md border bg-card"
      >
        {attachments.map((a) => (
          <AttachmentRow
            key={a.id}
            attachment={a}
            onOpen={() => setOpenId(a.id)}
          />
        ))}
      </ul>
      <Sheet
        open={active !== null}
        onOpenChange={(next) => {
          if (!next) setOpenId(null);
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-lg"
        >
          {active ? <AttachmentDetails attachment={active} /> : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

function AttachmentRow({
  attachment,
  onOpen,
}: {
  attachment: AttachmentMeta;
  onOpen: () => void;
}) {
  const mimeShort = shortMime(attachment.contentType);
  return (
    <li
      data-testid="attachment-card"
      className="flex items-center gap-3 px-3 py-2.5"
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
        {attachment.previewKind === "image" ? (
          <ImageIcon className="size-4" />
        ) : attachment.previewKind === "text" ? (
          <PaperclipIcon className="size-4" />
        ) : (
          <FileIcon className="size-4" />
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-foreground">
          {attachment.originalName}
        </span>
        <span
          className="truncate text-xs text-muted-foreground"
          title={attachment.contentType || undefined}
        >
          {mimeShort}
        </span>
      </div>
      <span className="hidden font-mono text-xs tabular-nums text-muted-foreground sm:inline">
        {formatBytes(attachment.size)}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Open details"
                data-testid="attachment-details"
                onClick={onOpen}
              />
            }
          >
            <InfoIcon />
          </TooltipTrigger>
          <TooltipContent>Open details</TooltipContent>
        </Tooltip>
        <AttachmentDownloadButton attachment={attachment} />
      </div>
    </li>
  );
}

function AttachmentDownloadButton({
  attachment,
}: {
  attachment: AttachmentMeta;
}) {
  const token = getAdminToken();
  const downloadUrl = api.attachmentDownloadUrl(attachment.id);
  const [downloading, setDownloading] = useState(false);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Download ${attachment.originalName}`}
            data-testid="attachment-download"
            disabled={downloading}
            onClick={(e) => {
              e.preventDefault();
              if (!token) {
                window.location.href = downloadUrl;
                return;
              }
              setDownloading(true);
              api
                .fetchAttachment(attachment.id)
                .then(async (res) => {
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = attachment.originalName;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
                })
                .catch((err) => toast.error(err?.message ?? "Download failed"))
                .finally(() => setDownloading(false));
            }}
          />
        }
      >
        <DownloadIcon />
      </TooltipTrigger>
      <TooltipContent>Download</TooltipContent>
    </Tooltip>
  );
}

function AttachmentDetails({ attachment }: { attachment: AttachmentMeta }) {
  return (
    <>
      <SheetHeader className="border-b">
        <SheetTitle>{attachment.originalName}</SheetTitle>
        <SheetDescription>
          {shortMime(attachment.contentType)} ·{" "}
          {formatBytes(attachment.size)} · {attachment.category}
        </SheetDescription>
      </SheetHeader>
      <div className="flex flex-col gap-4 overflow-y-auto px-4 py-4">
        <AttachmentPreview attachment={attachment} />
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <Meta
            label="MIME type"
            value={attachment.contentType || "—"}
            monospace
          />
          <Meta
            label="Category"
            value={attachment.category || "—"}
          />
          <Meta label="Size" value={formatBytes(attachment.size)} monospace />
          <Meta
            label="Created"
            value={formatExact(attachment.createdAt)}
            monospace
          />
        </dl>
        <HashField label="SHA-256" value={attachment.checksum} />
        <div className="flex items-center justify-end gap-2 pt-2">
          <SheetClose
            render={<Button type="button" variant="outline">Close</Button>}
          />
          <AttachmentDownloadButton attachment={attachment} />
        </div>
      </div>
    </>
  );
}

function HashField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const display = value ? `${value.slice(0, 16)}…${value.slice(-8)}` : "—";
  async function copy() {
    if (!value) return;
    const result = await copyToClipboard(value);
    if (!result.ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="flex flex-col gap-1.5 sm:col-span-2">
      <span className="text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 truncate rounded-md border bg-muted px-2 py-1 font-mono text-xs text-foreground"
          title={value || undefined}
        >
          {display}
        </code>
        {value ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Copy ${label}`}
                  disabled={copied}
                  onClick={copy}
                />
              }
            >
              {copied ? (
                <CheckIcon className="text-success" />
              ) : (
                <CopyIcon />
              )}
            </TooltipTrigger>
            <TooltipContent>{copied ? "Copied" : `Copy ${label}`}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}

function AttachmentPreview({ attachment }: { attachment: AttachmentMeta }) {
  if (attachment.previewKind === "image") {
    return <ImagePreview attachment={attachment} />;
  }
  if (attachment.previewKind === "text") {
    return <TextPreview attachment={attachment} />;
  }
  return (
    <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
      Preview not available for this file type.
    </div>
  );
}

function ImagePreview({ attachment }: { attachment: AttachmentMeta }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setSrc(null);
    setError(null);
    api
      .fetchAttachment(attachment.id)
      .then(async (res) => {
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? "Failed to load preview");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id]);

  if (error) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-md bg-muted/40 px-3 py-3 text-xs">
        <span className="text-destructive">{error}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setError(null);
            setSrc(null);
          }}
        >
          Retry
        </Button>
      </div>
    );
  }
  if (!src) {
    return <Skeleton className="h-48 w-full" />;
  }
  return (
    <div className="overflow-hidden rounded-md border bg-checker">
      <img
        src={src}
        alt={attachment.originalName}
        className="block max-h-96 w-full object-contain"
        loading="lazy"
      />
    </div>
  );
}

function TextPreview({ attachment }: { attachment: AttachmentMeta }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [wrap, setWrap] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    api
      .fetchAttachment(attachment.id)
      .then(async (res) => {
        const total = Number(res.headers.get("Content-Length")) || attachment.size;
        const cap = 16 * 1024;
        const reader = res.body?.getReader();
        if (!reader) {
          setText("");
          return;
        }
        const decoder = new TextDecoder("utf-8", { fatal: false });
        let received = 0;
        let out = "";
        while (received < cap) {
          const { value, done } = await reader.read();
          if (done) break;
          out += decoder.decode(value, { stream: true });
          received += value.byteLength;
        }
        try {
          reader.cancel();
        } catch {
          /* ignore */
        }
        if (cancelled) return;
        if (received >= cap && total > cap) {
          out += `\n\n… truncated (${total - received} more bytes)`;
        }
        setText(out);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? "Failed to load preview");
      });
    return () => {
      cancelled = true;
    };
  }, [attachment.id, attachment.size]);

  async function copy() {
    if (!text) return;
    const result = await copyToClipboard(text);
    if (!result.ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  if (error) {
    return (
      <div className="rounded-md bg-muted px-3 py-2 text-xs text-destructive">
        {error}
      </div>
    );
  }
  if (text === null) {
    return <Skeleton className="h-32 w-full" />;
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-1">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Toggle word wrap"
                onClick={() => setWrap((v) => !v)}
              />
            }
          >
            <ChevronRightIcon className={cn(wrap && "rotate-90")} />
          </TooltipTrigger>
          <TooltipContent>{wrap ? "Disable wrap" : "Enable wrap"}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Copy text"
                disabled={copied}
                onClick={copy}
              />
            }
          >
            {copied ? (
              <CheckIcon className="text-success" />
            ) : (
              <CopyIcon />
            )}
          </TooltipTrigger>
          <TooltipContent>{copied ? "Copied" : "Copy"}</TooltipContent>
        </Tooltip>
      </div>
      <pre
        className={cn(
          "max-h-96 overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed text-foreground",
          wrap ? "whitespace-pre-wrap" : "whitespace-pre",
        )}
      >
        {text}
      </pre>
    </div>
  );
}

function formatBytes(n: number): string {
  if (!n || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MiB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GiB`;
}

function shortMime(contentType: string): string {
  if (!contentType) return "—";
  return contentType.split(";")[0]?.trim() || contentType;
}

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "triaged", label: "Triaged" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "ignored", label: "Ignored" },
] as const;

const SEVERITY_OPTIONS = [
  { value: "unknown", label: "Unknown" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
] as const;

function ReportFieldPicker({
  label,
  current,
  options,
  busy,
  onPick,
  testIdPrefix,
}: {
  label: string;
  current: string;
  options: readonly { value: string; label: string }[];
  busy: boolean;
  onPick: (value: string) => void;
  testIdPrefix: string;
}) {
  const currentLabel =
    options.find((o) => o.value === current)?.label ?? current ?? "—";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-haspopup="menu"
            disabled={busy}
            data-testid={`${testIdPrefix}-picker`}
            className="min-w-[10rem] justify-between font-normal"
          />
        }
      >
        <span className="inline-flex items-center gap-2">
          <span className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            {label}
          </span>
          <span className="text-sm text-foreground">{currentLabel}</span>
        </span>
        <span aria-hidden="true" className="text-muted-foreground">
          ▾
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        <DropdownMenuGroup>
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={current}
            onValueChange={(value) => onPick(value)}
          >
            {options.map((opt) => (
              <DropdownMenuRadioItem
                key={opt.value}
                value={opt.value}
                data-testid={`${testIdPrefix}-option-${opt.value}`}
                disabled={busy}
              >
                {opt.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusEditor({
  value,
  busy,
  onChange,
  testIdPrefix,
}: {
  value: string;
  busy: boolean;
  onChange: (next: string) => void;
  testIdPrefix: string;
}) {
  return (
    <ReportFieldPicker
      label="Status"
      current={value}
      options={STATUS_OPTIONS}
      busy={busy}
      onPick={onChange}
      testIdPrefix={testIdPrefix}
    />
  );
}

function SeverityEditor({
  value,
  busy,
  onChange,
  testIdPrefix,
}: {
  value: string;
  busy: boolean;
  onChange: (next: string) => void;
  testIdPrefix: string;
}) {
  return (
    <ReportFieldPicker
      label="Severity"
      current={value}
      options={SEVERITY_OPTIONS}
      busy={busy}
      onPick={onChange}
      testIdPrefix={testIdPrefix}
    />
  );
}

// DropdownMenuItem is exported but only used here — keep the import used.
